import { useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useCookieConsent } from '../contexts/useCookieConsent';
import { Button } from '@/components/ui/button';

const PRIVACY_URL = 'https://www.kolrus.com/datenschutz/';

export default function CookieBanner() {
  const { t } = useLanguage();
  const {
    hasAnswered,
    settingsOpen,
    openSettings,
    closeSettings,
    acknowledge,
  } = useCookieConsent();

  useEffect(() => {
    if (!settingsOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') closeSettings();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [settingsOpen, closeSettings]);

  const showStrip = !hasAnswered && !settingsOpen;
  const showReopen = hasAnswered && !settingsOpen;

  return (
    <>
      {showStrip && (
        <div
          className="fixed inset-x-0 bottom-0 z-[100] border-t shadow-[0_-4px_24px_rgba(0,0,0,0.08)] backdrop-blur-md px-4 py-4 md:py-5"
          style={{
            backgroundColor: 'rgba(255, 255, 255, 0.98)',
            borderColor: 'rgba(169, 201, 232, 0.35)',
            paddingBottom: 'max(1rem, env(safe-area-inset-bottom))',
          }}
          role="dialog"
          aria-label={t('cookieBanner.ariaLabel')}
        >
          <div className="max-w-6xl mx-auto flex flex-col gap-4">
            <p className="text-xs sm:text-sm leading-relaxed" style={{ color: 'rgb(100, 130, 160)' }}>
              {t('cookieBanner.lead')}{' '}
              <a
                href={PRIVACY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="underline underline-offset-2 hover:opacity-80"
                style={{ color: 'rgb(107, 155, 200)' }}
              >
                {t('cookieBanner.privacy')}
              </a>
              .
            </p>
            <div className="flex flex-col sm:flex-row flex-wrap gap-2 sm:justify-end sm:items-center">
              <Button
                type="button"
                variant="outline"
                onClick={openSettings}
                className="w-full sm:w-auto rounded-lg border"
                style={{ borderColor: 'rgba(169, 201, 232, 0.5)', color: '#1B211A' }}
              >
                {t('cookieBanner.details')}
              </Button>
              <Button
                type="button"
                onClick={acknowledge}
                className="w-full sm:w-auto rounded-lg px-6 font-medium text-white border-0 shadow-sm"
                style={{ backgroundColor: '#6b9bc8' }}
              >
                {t('cookieBanner.acknowledge')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {settingsOpen && (
        <div
          className="fixed inset-0 z-[110] flex items-end sm:items-center justify-center p-4 sm:p-6"
          style={{ backgroundColor: 'rgba(27, 33, 26, 0.45)' }}
          role="presentation"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeSettings();
          }}
        >
          <div
            className="relative w-full max-w-lg max-h-[min(90vh,640px)] overflow-y-auto rounded-2xl shadow-xl border p-5 sm:p-6"
            style={{
              backgroundColor: '#FFFFFF',
              borderColor: 'rgba(169, 201, 232, 0.35)',
            }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="cookie-settings-title"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <h2
              id="cookie-settings-title"
              className="text-lg font-semibold mb-2 pr-8"
              style={{ color: '#1B211A' }}
            >
              {t('cookieBanner.settingsTitle')}
            </h2>
            <p className="text-xs sm:text-sm mb-4 leading-relaxed" style={{ color: 'rgb(100, 130, 160)' }}>
              {t('cookieBanner.settingsIntro')}
            </p>

            <ul className="space-y-3 mb-4 text-xs leading-relaxed" style={{ color: 'rgb(100, 130, 160)' }}>
              <li className="rounded-xl p-3" style={{ backgroundColor: 'rgba(169, 201, 232, 0.08)', border: '1px solid rgba(169, 201, 232, 0.25)' }}>
                <span className="font-medium" style={{ color: '#1B211A' }}>{t('cookieBanner.storageAuthTitle')}</span>
                {' — '}
                {t('cookieBanner.storageAuthDesc')}
              </li>
              <li className="rounded-xl p-3" style={{ border: '1px solid rgba(169, 201, 232, 0.25)' }}>
                <span className="font-medium" style={{ color: '#1B211A' }}>{t('cookieBanner.storageLanguageTitle')}</span>
                {' — '}
                {t('cookieBanner.storageLanguageDesc')}
              </li>
              <li className="rounded-xl p-3" style={{ border: '1px solid rgba(169, 201, 232, 0.25)' }}>
                <span className="font-medium" style={{ color: '#1B211A' }}>{t('cookieBanner.storageConsentTitle')}</span>
                {' — '}
                {t('cookieBanner.storageConsentDesc')}
              </li>
              <li className="rounded-xl p-3" style={{ border: '1px solid rgba(169, 201, 232, 0.25)' }}>
                <span className="font-medium" style={{ color: '#1B211A' }}>{t('cookieBanner.storageSessionTitle')}</span>
                {' — '}
                {t('cookieBanner.storageSessionDesc')}
              </li>
            </ul>

            <p className="text-xs mb-6 leading-relaxed rounded-lg p-3" style={{ backgroundColor: 'rgba(169, 201, 232, 0.12)', color: 'rgb(100, 130, 160)' }}>
              {t('cookieBanner.noThirdParty')}
            </p>

            <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={closeSettings}
                className="w-full sm:w-auto rounded-lg"
                style={{ borderColor: 'rgba(169, 201, 232, 0.5)', color: '#1B211A' }}
              >
                {t('cookieBanner.cancel')}
              </Button>
              <Button
                type="button"
                onClick={acknowledge}
                className="w-full sm:w-auto rounded-lg text-white border-0"
                style={{ backgroundColor: '#6b9bc8' }}
              >
                {t('cookieBanner.acknowledge')}
              </Button>
            </div>

            <button
              type="button"
              className="absolute top-3 right-3 p-2 rounded-lg hover:bg-black/5 text-lg leading-none"
              style={{ color: '#647a90' }}
              aria-label={t('cookieBanner.cancel')}
              onClick={closeSettings}
            >
              ×
            </button>
          </div>
        </div>
      )}

      {showReopen && (
        <button
          type="button"
          onClick={openSettings}
          className="fixed z-[99] text-left text-[11px] sm:text-xs underline underline-offset-2 hover:opacity-80 max-w-[min(100vw-2rem,14rem)]"
          style={{
            left: 'max(1rem, env(safe-area-inset-left))',
            bottom: 'max(1rem, env(safe-area-inset-bottom))',
            color: 'rgb(100, 130, 160)',
          }}
        >
          {t('cookieBanner.reopenLink')}
        </button>
      )}
    </>
  );
}
